import React, { useEffect, useMemo, useState } from 'react';
import './service-request.css';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { IAccountBillTo } from '../../../models/account';
import { RootState } from '../../../store';
import { ISession, IConstants } from '../../../models/pro-sites';
import { loadBillToAccounts } from '../../../store/account/account-actions';
import { loadConstants } from '../../../store/externals/externals-actions';
import ServiceRequestSelect from './service-request-select';
import ServiceRequestShipping from './service-request-shipping';
import ServiceRequestSidebar from './service-request-sidebar';
import ServiceRequestAccountInfo from './service-request-account-info';
import ColorExchange from './color-exchange';
import {
    useBillToShipTo,
    useServiceOptions,
    useShippingOptions,
} from '../../../hooks/service-request-hooks';
import {
    IErrorSNState,
    IServiceRequestInitial,
    getSerialNumberInitial,
    getServiceRequestInitial,
    getShipToUpper,
    mapColorExchageStateToServiceRequest,
} from '../../../models/service-request';
import ServiceRequestSubmitted from './service-request-submitted';
import isValid, { ServiceRequestFormErrors } from './validation';
import { submitServiceRequest } from '../../../api/service-request-api';
import { IAccount } from '../../../models/company-account';
import { apiOpenPDF } from '../../../api/api-utils';

interface IServiceRequestCenterProps {
    session: ISession | null;
    billToAccounts: IAccountBillTo[] | [];
    loadBillToAccounts: () => Promise<void>;
    constants: IConstants | null;
    loadConstants: () => Promise<void>;
}
export const ServiceRequestCenter = ({
    session,
    billToAccounts,
    loadBillToAccounts,
    constants,
    loadConstants,
}: IServiceRequestCenterProps) => {
    const intl = useIntl();
    const [sessionBillTo, setSessionBillTo] = useState<IAccountBillTo>();
    const [serviceRequest, setServiceRequest] = useState(
        getServiceRequestInitial('exchange')
    );
    const [colorExchangeState, setColorExchangeState] = useState([
        getSerialNumberInitial(),
    ]);
    const [billToOptions, shipToOptions] = useBillToShipTo(
        billToAccounts,
        serviceRequest.billTo.AccountNumber
    );
    const [submitted, setSubmitted] = useState(false);
    const [validating, setValidating] = useState(false);
    const [errorSN, setErrorSN] = useState<IErrorSNState>({});
    const [errorState, setErrorState] = useState<ServiceRequestFormErrors>({});
    const serviceOptions = useServiceOptions();
    const shippingOptions = useShippingOptions();

    useEffect(() => {
        if (!constants) loadConstants();
        if (!billToAccounts.length) loadBillToAccounts();
    }, []);

    useEffect(() => {
        if (session && billToAccounts)
            setSessionBillTo(
                billToAccounts.find(
                    (acc) => acc.fullAccountNumber === session.billingAccount
                )
            );
    }, [billToAccounts]);
    useEffect(() => {
        if (
            session &&
            billToAccounts.length &&
            serviceOptions &&
            shippingOptions &&
            sessionBillTo
        )
            setServiceRequest((serviceRequest) => ({
                ...serviceRequest,
                contactName: session.firstName + ' ' + session.lastName,
                contactPhone: session.phoneNumber,
                serviceOption: serviceOptions[0].text,
                shippingOption: shippingOptions[0].text,
                billTo: {
                    AccountNumber: sessionBillTo!.accountNumber,
                    FullAccountNumber: sessionBillTo!.fullAccountNumber,
                    PartyName: sessionBillTo!.partyName,
                },
                shipTo: getShipToUpper(
                    sessionBillTo!.shipToAccounts.find(
                        (acc) => acc.accountNumber === session.shippingAccount
                    ) || ({} as IAccount)
                ),
            }));
    }, [billToAccounts.length, serviceOptions, shippingOptions, sessionBillTo]);

    useEffect(() => {
        if (Object.keys(errorState).length > 0) {
            const errors = isValid(serviceRequest, session!.country);
            setErrorState(errors);
        }
    }, [serviceRequest]);

    const saveServiceRequest = async () => {
        const dto: IServiceRequestInitial =
            mapColorExchageStateToServiceRequest(
                colorExchangeState,
                serviceRequest
            );
        const result = await submitServiceRequest(dto);
        apiOpenPDF(result);
        setSubmitted(true);
    };
    const handleSubmit = () => {
        setValidating(true);
        const errors = isValid(serviceRequest, session!.country);
        if (
            Object.keys(errors).length === 0 &&
            Object.values(errorSN).every((errorSN) => errorSN === false)
        ) {
            setValidating(false);
            saveServiceRequest();
        } else {
            setErrorState(errors);
        }
    };

    const errorMessage = useMemo(() => {
        if (!validating) return null;
        let message;
        if (Object.values(errorSN).some((errorSN) => errorSN === true)) {
            message = {
                id: 'Please_enter_into_before_submitting',
                defaultMessage:
                    'Please enter all required information before submitting',
            };
        } else if (Object.keys(errorState).length > 0) {
            message = {
                id: 'NONE',
                defaultMessage: Object.values(errorState)[0],
            };
        }

        return (
            message && (
                <div className="alert alert-danger">
                    <span>
                        <FormattedMessage {...message} />
                    </span>
                </div>
            )
        );
    }, [errorSN, errorState, validating]);

    return (
        <div className="row">
            {!submitted ? (
                <div className="col-md-9">
                    <ServiceRequestSelect />
                    {constants &&
                        billToOptions &&
                        shipToOptions &&
                        serviceOptions &&
                        shippingOptions && (
                            <>
                                <ColorExchange
                                    serviceRequest={serviceRequest}
                                    setServiceRequest={setServiceRequest}
                                    colorExchangeState={colorExchangeState}
                                    setColorExchangeState={
                                        setColorExchangeState
                                    }
                                    validating={validating}
                                    errorSN={errorSN}
                                    setErrorSN={setErrorSN}
                                />
                                <ServiceRequestAccountInfo
                                    session={session}
                                    constants={constants}
                                    serviceRequest={serviceRequest}
                                    setServiceRequest={setServiceRequest}
                                    billToAccounts={billToAccounts}
                                    billToOptions={billToOptions}
                                    shipToOptions={shipToOptions}
                                    serviceOptions={serviceOptions}
                                    shippingOptions={shippingOptions}
                                    errors={errorState}
                                />
                                <ServiceRequestShipping
                                    session={session}
                                    setServiceRequest={setServiceRequest}
                                    shippingLabel={serviceRequest.shippingLabel}
                                />
                                {errorMessage}
                                <button
                                    className="btn btn-default gutter-top btn-service-request"
                                    name="submitServiceRequest"
                                    type="button"
                                    onClick={() => handleSubmit()}
                                >
                                    {intl.formatMessage({
                                        id: 'Continue',
                                        defaultMessage: 'Continue',
                                    })}
                                </button>
                            </>
                        )}
                </div>
            ) : (
                <ServiceRequestSubmitted setSubmitted={setSubmitted} />
            )}
            <div className="col-xs-12 col-md-3">
                <ServiceRequestSidebar session={session} />
            </div>
        </div>
    );
};

const mapStateToProps = (state: RootState) => ({
    session: state.session ? state.session.data! : null,
    billToAccounts: state.billToAccounts ? state.billToAccounts.data! : [],
    constants: state.constants ? state.constants : null,
});

const mapDispatchToProps = {
    loadBillToAccounts,
    loadConstants,
};

const serviceRequestCenterContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(ServiceRequestCenter);

export default serviceRequestCenterContainer;
